import { Box } from '@mui/material'
import React from 'react'
import {
  TABLE_WIDTH_INVENTORY_BRAND_FILTER,
  TABLE_WIDTH_INVENTORY_DATE_FILTER,
  TABLE_WIDTH_INVENTORY_DAYS,
  TABLE_WIDTH_INVENTORY_KM,
  TABLE_WIDTH_INVENTORY_LEAD,
  TABLE_WIDTH_INVENTORY_MODEL_FILTER,
  TABLE_WIDTH_INVENTORY_PARTNER,
  TABLE_WIDTH_INVENTORY_PLATE,
  TABLE_WIDTH_INVENTORY_PRICE_CHANGE_COUNT,
  TABLE_WIDTH_INVENTORY_SUGGESTED_PRICE,
} from 'lib/tableLib'

import TableHeaderCell from '../../atoms/TableHeaderCell'
import FilterSelect from '../../atoms/FilterSelect'
import FilterRange from '../../atoms/FilterRange'
import FilterDateRange from '../../atoms/FilterDateRange'
import DateCell from '../../atoms/DateCell'
import Tooltip from '../../atoms/Tooltip'

export const createRepriceTableColumns = (
  partnerInventoryFilters,
  countryConfig,
  numberFormatter,
  t
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const {
    filters,
    filtersData,
    setDateRange,
    setKmRange,
    setBrands,
    setModels,
  } = partnerInventoryFilters

  return [
    {
      accessor: 'brand',
      Header: (
        <TableHeaderCell
          label={t('inventoryReprice.table.headers.brand')}
          filter={
            <FilterSelect
              data-testid="filter-brands"
              options={filtersData.brands}
              value={filters.brands}
              handleChange={setBrands}
              multiple
            />
          }
        />
      ),
      width: TABLE_WIDTH_INVENTORY_BRAND_FILTER,
      sticky: 'left',
    },
    {
      accessor: 'model',
      Header: (
        <TableHeaderCell
          label={t('inventoryReprice.table.headers.model')}
          filter={
            <FilterSelect
              options={filtersData.models}
              value={filters.models}
              handleChange={setModels}
              multiple
            />
          }
        />
      ),
      width: TABLE_WIDTH_INVENTORY_MODEL_FILTER,
      sticky: 'left',
    },
    {
      accessor: 'date',
      Header: (
        <TableHeaderCell
          label={t('inventoryReprice.table.headers.date')}
          filter={
            <FilterDateRange
              value={[filters.dateFrom, filters.dateTo]}
              handleChange={setDateRange}
              data-testid="filter-date"
            />
          }
        />
      ),
      Cell: DateCell,
      width: TABLE_WIDTH_INVENTORY_DATE_FILTER,
      sticky: 'left',
    },
    {
      accessor: 'km',
      Header: (
        <TableHeaderCell
          label={t('inventoryReprice.table.headers.km')}
          filter={
            <FilterRange
              minBound={filtersData.kmMin}
              maxBound={filtersData.kmMax}
              min={filters.kmMin}
              max={filters.kmMax}
              handleRangeChange={setKmRange}
            />
          }
        />
      ),
      Cell: ({ value }) => (value && numberFormatter.format(value)) || null,
      width: TABLE_WIDTH_INVENTORY_KM,
      cellProps: {
        sx: {
          pr: 0,
        },
      },
      sticky: 'left',
    },
    {
      accessor: 'partner',
      Header: (
        <TableHeaderCell label={t('inventoryReprice.table.headers.partner')} />
      ),
      Cell: ({ row }) => (
        <span id={`${row.original.partnerId}`}>
          {row.original.partnerName}{' '}
        </span>
      ),
      width: TABLE_WIDTH_INVENTORY_PARTNER,
    },
    ...(countryConfig.tableShow.vin
      ? [
          {
            accessor: 'vin',
            Header: (
              <TableHeaderCell
                label={t('inventoryReprice.table.headers.vin')}
              />
            ),
            Cell: ({ row, value }) => {
              const days = row.original.days
              const previewDays = row.original.previewDays || 0
              const halfPreviewDays = previewDays / 2

              let color = days >= 0 ? '#006d1d' : 'inherited'

              if (days > previewDays) {
                color = '#c40000'
              } else if (days > halfPreviewDays) {
                color = '#d58e0a'
              }
              const background = {
                color,
              }
              return (
                <Box>
                  <Tooltip
                    title={row.original.partnerAddress}
                    followCursor
                    placement="bottom-start"
                  >
                    <Box
                      {...(countryConfig.attribute.primary === 'vin'
                        ? { sx: { ...background } }
                        : {})}
                    >
                      {value}
                    </Box>
                  </Tooltip>
                </Box>
              )
            },
            width: 180,
          },
        ]
      : []),
    ...(countryConfig.tableShow.plate
      ? [
          {
            accessor: 'plate',
            Header: (
              <TableHeaderCell
                label={t('inventoryReprice.table.headers.plate')}
              />
            ),
            Cell: ({ row, value }) => {
              const days = row.original.days
              const previewDays = row.original.previewDays || 0
              const halfPreviewDays = previewDays / 2

              let color = days >= 0 ? '#006d1d' : 'inherited'

              if (days > previewDays) {
                color = '#c40000'
              } else if (days > halfPreviewDays) {
                color = '#d58e0a'
              }
              const background = {
                color,
              }

              return (
                <Box>
                  <Tooltip
                    title={row.original.partnerAddress}
                    followCursor
                    placement="bottom-start"
                  >
                    <Box
                      {...(countryConfig.attribute.primary === 'plate'
                        ? { sx: { ...background } }
                        : {})}
                    >
                      {value}
                    </Box>
                  </Tooltip>
                </Box>
              )
            },
            width: TABLE_WIDTH_INVENTORY_PLATE,
          },
        ]
      : []),
    {
      accessor: 'priceChangeCount',
      Header: (
        <TableHeaderCell
          label={t('inventoryReprice.table.headers.hasPriceChanges')}
          labelProps={{ sx: { textWrap: 'auto' } }}
        />
      ),
      Cell: ({ value }) => {
        return (
          value &&
          value > 1 && <Box textAlign="center">{value ? value - 1 : ''}</Box>
        )
      },
      width: TABLE_WIDTH_INVENTORY_PRICE_CHANGE_COUNT,
    },
    {
      accessor: 'days',
      Header: (
        <TableHeaderCell
          label={t('inventoryReprice.table.headers.days')}
          labelProps={{ sx: { textWrap: 'auto' } }}
        />
      ),
      width: TABLE_WIDTH_INVENTORY_DAYS,
    },
    {
      accessor: 'lead',
      Header: (
        <TableHeaderCell
          label={t('inventoryReprice.table.headers.lead')}
          labelProps={{ sx: { textWrap: 'auto' } }}
        />
      ),
      width: TABLE_WIDTH_INVENTORY_LEAD,
    },
    {
      accessor: 'suggestedPrice',
      Header: (
        <TableHeaderCell
          label={t('inventoryReprice.table.headers.price')}
          labelProps={{ sx: { textWrap: 'auto' } }}
        />
      ),
      Cell: ({ value }) => (value && numberFormatter.format(value)) || null,
      width: TABLE_WIDTH_INVENTORY_SUGGESTED_PRICE,
    },
    {
      Header: (
        <TableHeaderCell
          label={t('inventoryReprice.table.headers.newPrice')}
          labelProps={{ sx: { textWrap: 'auto' } }}
        />
      ),
      accessor: 'newPrice',
      width: 130,
    },
    {
      accessor: 'update',
      width: 135,
    },
  ]
}
